import {
  CHAT_LIST_KIND_QUERY_KEY_SEGMENT,
  CHAT_LIST_QUERY_KEY,
  CHAT_SEARCH_MIN_QUERY_LENGTH,
  DEFAULT_CHAT_LIST_LIMIT,
  DEFAULT_CHAT_LIST_OFFSET,
} from "@frontend-shared/constants";
import type { IGetChatListParams } from "@frontend-shared/types";
import { useInfiniteQuery } from "@tanstack/react-query";

import { chatApi } from "@/api/services";

interface IUseChatListParams extends Pick<
  IGetChatListParams,
  "search" | "kind"
> {
  folderId?: string;
  enabled?: boolean;
}

export const useChatList = ({
  search,
  folderId,
  kind,
  enabled = true,
}: IUseChatListParams = {}) => {
  const queryKey = [CHAT_LIST_QUERY_KEY];
  // NOTE: NSAI-5678 — the sidebar keeps its regular and assistant lists in
  // separate, separately paginated caches. Only the segment is added, so a
  // `[CHAT_LIST_QUERY_KEY]` prefix invalidation or patch still reaches both.
  if (kind !== undefined) {
    queryKey.push(CHAT_LIST_KIND_QUERY_KEY_SEGMENT, kind);
  }
  // NOTE: Include folder segment in query key to separate folder-specific cache from root cache
  if (folderId !== undefined) {
    queryKey.push("folder", folderId ?? null);
  }
  if (search) {
    queryKey.push(search);
  }
  // NOTE: NSAI-4334 — an empty search means "browse" and must still fire; a
  // sub-minimum one must not. Chat titles and message content are encrypted, so
  // the server matches them with a bounded decrypt-and-scan that costs the same
  // for one character as for a whole word. The server refuses these too — that
  // is the guard that protects the database, since web is one of three clients
  // — so this only saves a round-trip that could not return anything.
  const trimmedSearch = search?.trim() ?? "";
  const isSearchBelowMinLength =
    trimmedSearch.length > 0 &&
    trimmedSearch.length < CHAT_SEARCH_MIN_QUERY_LENGTH;

  return useInfiniteQuery({
    queryKey,
    enabled: enabled && !isSearchBelowMinLength,
    queryFn: ({ pageParam }) => {
      const limit = DEFAULT_CHAT_LIST_LIMIT;
      const offset = pageParam * limit;
      return chatApi.getChatList({
        limit,
        offset,
        search,
        folderId,
        kind,
      });
    },
    initialPageParam: DEFAULT_CHAT_LIST_OFFSET,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasNextPage ? allPages.length : undefined;
    },
  });
};
